import { useState } from "react";
import type { PricingRule, DayOfWeek } from "../../api/location.ts";
import {
  ALL_DAYS,
  validatePricingRules,
  getPreviewForDay,
  type PricingRuleErrors,
} from "./pricingRulesLogic.ts";

const DAY_FULL: Record<DayOfWeek, string> = {
  Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday",
  Fri: "Friday", Sat: "Saturday", Sun: "Sunday",
};
import styles from "../../styles/formPage.module.scss";

/* ── Public tab component ─────────────────────────────────── */

export function LocationPricingRulesTab({
  rules,
  onChange,
}: {
  rules: PricingRule[];
  onChange: (rules: PricingRule[]) => void;
}) {
  const [previewDay, setPreviewDay] = useState<DayOfWeek>("Mon");
  const [previewMode, setPreviewMode] = useState<"day" | "week">("day");

  const { ruleErrors } = validatePricingRules(rules);
  const preview = getPreviewForDay(rules, previewDay);

  function addRule() {
    onChange([
      ...rules,
      { id: crypto.randomUUID(), days: [], startTime: "", endTime: "", percentage: 0, roundingMode: "none", roundingEnding: 0 },
    ]);
  }

  function deleteRule(id: string) {
    onChange(rules.filter((r) => r.id !== id));
  }

  function duplicateRule(id: string) {
    const idx = rules.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const orig = rules[idx]!;
    const dup: PricingRule = { ...orig, id: crypto.randomUUID() };
    const next = [...rules];
    next.splice(idx + 1, 0, dup);
    onChange(next);
  }

  function updateRule(id: string, patch: Partial<PricingRule>) {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>Pricing Rules</h3>
      <p className={styles.description}>
        Automatically adjust product prices based on day of week and time range.
        Rules must not overlap on the same day.
      </p>

      {rules.length === 0 ? (
        <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: "var(--space-3)" }}>
          No pricing rules configured. Click &ldquo;Add Rule&rdquo; to create one.
        </p>
      ) : (
        rules.map((rule, idx) => (
          <PricingRuleCard
            key={rule.id}
            rule={rule}
            errors={ruleErrors.get(rule.id)}
            index={idx}
            onUpdate={(patch) => updateRule(rule.id, patch)}
            onDelete={() => deleteRule(rule.id)}
            onDuplicate={() => duplicateRule(rule.id)}
          />
        ))
      )}

      <button
        type="button"
        className={styles.secondaryBtn}
        style={{ marginBottom: "var(--space-5)" }}
        onClick={addRule}
      >
        + Add Rule
      </button>

      {/* Preview ─────────────────────────────────────────── */}
      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-2)" }}>Schedule Preview</h3>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: "var(--space-3)", border: "1px solid var(--color-border-hover)", borderRadius: "var(--radius-1)", width: "fit-content", overflow: "hidden" }}>
        {(["day", "week"] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setPreviewMode(mode)}
            style={{
              padding: "var(--space-1) var(--space-3)",
              fontSize: "var(--text-sm)",
              border: "none",
              cursor: "pointer",
              background: previewMode === mode ? "var(--color-primary)" : "var(--color-bg)",
              color: previewMode === mode ? "var(--color-primary-foreground)" : "var(--color-text)",
              fontWeight: previewMode === mode ? "var(--font-medium)" : undefined,
            }}
          >
            {mode === "day" ? "Day" : "Full Week"}
          </button>
        ))}
      </div>

      {previewMode === "day" ? (
        <>
          <div className={styles.fieldRow} style={{ marginBottom: "var(--space-3)" }}>
            <label>Day</label>
            <select
              className={styles.fieldSelect}
              value={previewDay}
              onChange={(e) => setPreviewDay(e.target.value as DayOfWeek)}
              style={{ maxWidth: 160 }}
            >
              {ALL_DAYS.map((d) => (
                <option key={d} value={d}>{DAY_FULL[d]}</option>
              ))}
            </select>
          </div>

          {preview.length === 0 ? (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
              No rules apply to {DAY_FULL[previewDay]}.
            </p>
          ) : (
            <PreviewTable rows={preview} />
          )}
        </>
      ) : (
        <WeekPreview rules={rules} />
      )}
    </div>
  );
}

/* ── Rule card ────────────────────────────────────────────── */

function PricingRuleCard({
  rule,
  errors,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
}: {
  rule: PricingRule;
  errors: PricingRuleErrors | undefined;
  index: number;
  onUpdate: (patch: Partial<PricingRule>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const hasErrors = errors && Object.keys(errors).length > 0;

  return (
    <div
      style={{
        border: `1px solid ${hasErrors ? "var(--color-error)" : "var(--color-border-default)"}`,
        borderRadius: "var(--radius-2)",
        padding: "var(--space-4)",
        background: hasErrors ? "var(--color-error-muted)" : "var(--color-bg)",
        marginBottom: "var(--space-3)",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
        <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)", color: "var(--color-text-secondary)" }}>
          Rule {index + 1}
        </span>
        <div style={{ display: "flex", gap: "var(--space-1)" }}>
          <button
            type="button"
            className={styles.secondaryBtn}
            style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
            onClick={onDuplicate}
          >
            Duplicate
          </button>
          <button
            type="button"
            className={styles.dangerBtn}
            style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Days */}
      <div style={{ marginBottom: "var(--space-3)" }}>
        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-medium)", color: "var(--color-text-secondary)", display: "block", marginBottom: "var(--space-1)" }}>
          Days
        </span>
        <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
          {ALL_DAYS.map((day) => {
            const selected = rule.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                onClick={() => {
                  const next = selected
                    ? rule.days.filter((d) => d !== day)
                    : [...rule.days, day];
                  onUpdate({ days: next });
                }}
                style={{
                  padding: "var(--space-1) var(--space-2)",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-medium)",
                  minWidth: 40,
                  border: "1px solid",
                  borderColor: selected ? "var(--color-primary)" : "var(--color-border-hover)",
                  borderRadius: "var(--radius-1)",
                  background: selected ? "var(--color-primary)" : "var(--color-bg)",
                  color: selected ? "var(--color-primary-foreground)" : "var(--color-text)",
                  cursor: "pointer",
                }}
              >
                {day}
              </button>
            );
          })}
        </div>
        {errors?.days && <FieldError message={errors.days} />}
      </div>

      {/* Time range */}
      <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", marginBottom: "var(--space-3)" }}>
        <div style={{ flex: 1 }}>
          <FieldLabel text="Start Time" />
          <input
            type="time"
            className={styles.fieldInput}
            value={rule.startTime}
            onChange={(e) => onUpdate({ startTime: e.target.value })}
          />
          {errors?.startTime && <FieldError message={errors.startTime} />}
        </div>
        <div style={{ alignSelf: "flex-end", paddingBottom: "var(--space-2)", color: "var(--color-text-secondary)", fontSize: "var(--text-sm)" }}>
          —
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel text="End Time" />
          <input
            type="time"
            className={styles.fieldInput}
            value={rule.endTime}
            onChange={(e) => onUpdate({ endTime: e.target.value })}
          />
          {errors?.endTime && <FieldError message={errors.endTime} />}
        </div>
      </div>

      {/* Percentage */}
      <div>
        <FieldLabel text="Price Adjustment (%)" />
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <input
            type="number"
            className={styles.fieldInput}
            style={{ maxWidth: 120 }}
            value={isNaN(rule.percentage) ? "" : rule.percentage}
            min={-50}
            max={1000}
            step={1}
            onChange={(e) => onUpdate({ percentage: parseFloat(e.target.value) })}
          />
          <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>%</span>
          {!isNaN(rule.percentage) && (
            <span style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              color: rule.percentage >= 0 ? "var(--color-success)" : "var(--color-error)",
            }}>
              {rule.percentage > 0 ? `+${rule.percentage}%` : `${rule.percentage}%`}
            </span>
          )}
        </div>
        {errors?.percentage && <FieldError message={errors.percentage} />}
      </div>

      {/* Rounding */}
      <div style={{ marginTop: "var(--space-3)" }}>
        <FieldLabel text="Price Rounding" />
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <select
            className={styles.fieldSelect}
            style={{ maxWidth: 200 }}
            value={rule.roundingMode}
            onChange={(e) => {
              const mode = e.target.value as PricingRule["roundingMode"];
              // Default a sensible ending the first time "ending" is chosen.
              onUpdate({
                roundingMode: mode,
                roundingEnding: mode === "ending" && !rule.roundingEnding ? 0.99 : rule.roundingEnding,
              });
            }}
          >
            <option value="none">No rounding</option>
            <option value="nearest">Nearest whole (→ 13)</option>
            <option value="down">Down to whole (→ 12)</option>
            <option value="ending">End in…</option>
          </select>

          {rule.roundingMode === "ending" && (
            <>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>ending</span>
              <input
                type="number"
                className={styles.fieldInput}
                style={{ maxWidth: 90 }}
                value={isNaN(rule.roundingEnding) ? "" : rule.roundingEnding}
                min={0}
                max={0.99}
                step={0.01}
                onChange={(e) => onUpdate({ roundingEnding: parseFloat(e.target.value) })}
              />
              <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                e.g. 0.99, 0.49, 0.95
              </span>
            </>
          )}
        </div>
        {errors?.roundingEnding && <FieldError message={errors.roundingEnding} />}
      </div>

      {/* Overlap error */}
      {errors?.overlap && (
        <div style={{ marginTop: "var(--space-2)" }}>
          <FieldError message={errors.overlap} bold />
        </div>
      )}
    </div>
  );
}

/* ── Preview components ───────────────────────────────────── */

function PreviewTable({ rows }: { rows: { startTime: string; endTime: string; percentage: number }[] }) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}>Start</th>
          <th className={styles.th}>End</th>
          <th className={styles.th}>Adjustment</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={i}>
            <td className={styles.td}>{p.startTime}</td>
            <td className={styles.td}>{p.endTime}</td>
            <td className={styles.td} style={{ color: p.percentage >= 0 ? "var(--color-success)" : "var(--color-error)", fontWeight: "var(--font-medium)" }}>
              {p.percentage > 0 ? `+${p.percentage}%` : `${p.percentage}%`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WeekPreview({ rules }: { rules: PricingRule[] }) {
  const hasAnyRule = ALL_DAYS.some((d) => getPreviewForDay(rules, d).length > 0);

  if (!hasAnyRule) {
    return (
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
        No pricing rules configured yet.
      </p>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "var(--space-3)" }}>
      {ALL_DAYS.map((day) => {
        const rows = getPreviewForDay(rules, day);
        return (
          <div
            key={day}
            style={{
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-2)",
              overflow: "hidden",
            }}
          >
            <div style={{
              padding: "var(--space-2) var(--space-3)",
              background: "var(--color-bg-muted)",
              borderBottom: "1px solid var(--color-border-default)",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-semibold)",
              color: "var(--color-text)",
            }}>
              {DAY_FULL[day]}
            </div>
            {rows.length === 0 ? (
              <p style={{ padding: "var(--space-2) var(--space-3)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)", margin: 0 }}>
                No rules
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {rows.map((p, i) => (
                    <tr key={i} style={{ borderTop: i > 0 ? "1px solid var(--color-bg-muted)" : undefined }}>
                      <td style={{ padding: "var(--space-1) var(--space-3)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                        {p.startTime} – {p.endTime}
                      </td>
                      <td style={{ padding: "var(--space-1) var(--space-3)", fontSize: "var(--text-xs)", fontWeight: "var(--font-medium)", textAlign: "right", whiteSpace: "nowrap", color: p.percentage >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
                        {p.percentage > 0 ? `+${p.percentage}%` : `${p.percentage}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Small helpers ────────────────────────────────────────── */

function FieldLabel({ text }: { text: string }) {
  return (
    <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-medium)", color: "var(--color-text-secondary)", display: "block", marginBottom: "var(--space-1)" }}>
      {text}
    </span>
  );
}

function FieldError({ message, bold }: { message: string; bold?: boolean }) {
  return (
    <p style={{
      fontSize: "var(--text-xs)",
      color: "var(--color-error)",
      marginTop: "var(--space-1)",
      fontWeight: bold ? "var(--font-medium)" : undefined,
    }}>
      {message}
    </p>
  );
}
